import { useEffect, useState } from 'react';

import versionApi from '@/services/api/version';

/**
 * Versions-Check gegen den Server — Datenquelle fuers Reload-Banner.
 * Hintergrund + Messung: docs/tasks/version-skew-frontend.md.
 *
 * Verglichen wird der in dieses Bundle einkompilierte Build-SHA
 * (`REACT_APP__SENTRY_RELEASE`, vom Deploy gesetzt) gegen `fe_build` aus
 * `GET /api/version` — die Version des Bundles, das der Server GERADE
 * AUSLIEFERT. Weichen sie ab, laeuft in diesem Tab ein altes Bundle.
 *
 * AUSDRUECKLICH NICHT gegen `build` (Git-HEAD des Deploys): der wandert auch
 * bei einem BE-only-Deploy weiter, waehrend das Bundle unveraendert bleibt. Der
 * alte Vergleich meldete dann eine Abweichung, die es nicht gab — nach Deploy
 * #101 stand das Banner dadurch PERMANENT bei allen Nutzern, und ohne
 * FE-Deploy haette sich daran auch nichts mehr geaendert.
 *
 * FAIL-SILENT ist Absicht: kein eigener Stand, kein Serverstand oder ein
 * Netzfehler fuehren zu `false`, nie zu einem Banner. Ein falsch positives
 * „neue Version" schickt einen Agenten mitten im Kundengespraech in einen
 * Reload — teurer als ein verpasster Hinweis, den der naechste Turnus nachholt.
 *
 * `serverBuild` ist der zuletzt gemessene ABWEICHENDE Serverstand (sonst
 * `null`). Daran haengt der Reload beim Seitenwechsel
 * (useNeuladenBeimSeitenwechsel, docs/tasks/auto-reload-neuer-stand.md).
 */
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

const useVersionCheck = (): { updateAvailable: boolean; serverBuild: string | null } => {
  const [serverBuild, setServerBuild] = useState<string | null>(null);

  // Zur Build-Zeit von CRA inlined. Leer bei lokalen Builds.
  const localBuild = process.env.REACT_APP__SENTRY_RELEASE ?? '';

  useEffect(() => {
    // Ohne eigenen Stand gibt es nichts zu vergleichen — dann auch keine
    // Anfrage.
    //
    // 🔥 Nach der Erkennung wird WEITER gemessen (Entscheid Alex 27.10.2026).
    // Bis dahin hoerte der Hook hier auf. Seit dem Reload beim Seitenwechsel
    // gibt es aber eine Erkenntnis, die nur der weitere Turnus liefert: ein
    // ZWEITER Deploy. Hat die Sperre den Reload fuer Stand X schon verbraucht,
    // darf erst ein neuer Stand Y wieder neu laden — ohne weiteres Messen saehe
    // der Tab Y nie.
    if (!localBuild) {
      return undefined;
    }

    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        const { fe_build: serverBundle } = await versionApi.getVersion();
        if (cancelled || !serverBundle) {
          return;
        }
        // Gleicher Stand (auch nach einem Rollback) heisst: kein neuer Build.
        setServerBuild(serverBundle !== localBuild ? serverBundle : null);
      } catch {
        // Netzfehler/401/Timeout: schweigen und beim naechsten Turnus erneut
        // messen. Raten waere hier schlechter als schweigen.
      }
    };

    const checkOnVisible = (): void => {
      if (document.visibilityState !== 'hidden') {
        void check();
      }
    };

    void check();

    const intervalId = window.setInterval(() => void check(), VERSION_POLL_INTERVAL_MS);
    // `focus` = Fensterwechsel, `visibilitychange` = Wechsel zwischen Browser-
    // TABS. Der stale Tab vom 22.07. lag im zweiten Fall: Fenster offen, Tab
    // im Hintergrund.
    window.addEventListener('focus', checkOnVisible);
    document.addEventListener('visibilitychange', checkOnVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkOnVisible);
      document.removeEventListener('visibilitychange', checkOnVisible);
    };
  }, [localBuild]);

  return { updateAvailable: serverBuild !== null, serverBuild };
};

export default useVersionCheck;
